import React, { useState, useEffect, useRef } from "react";
import { MOCK } from "./data.jsx";
import { Ico, IASMeter } from "./primitives.jsx";
import { apiPost, apiGet, ApiError, isDemoMode, navigate } from "./api.jsx";

export function Analyzer() {
  const [step, setStep] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState(null);
  const steps = ["Upload", "Extract", "Review", "Score"];
  return (
    <div className="page route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>New analysis</div>
          <h1 className="page-title">Submit a deck for <em>evaluation</em>.</h1>
          <p className="page-sub" style={{maxWidth:640, marginTop:10}}>
            We'll extract structure, triangulate against public sources, score it, and produce a memo-ready report. Typical turnaround: 11 minutes.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div style={{display:"flex", gap:0, marginBottom:32, borderTop:"1px solid var(--line)", borderBottom:"1px solid var(--line)"}}>
        {steps.map((s, i) => (
          <div key={s}
               onClick={()=>setStep(i)}
               style={{
                 flex:1, padding:"16px 20px", cursor:"pointer",
                 borderRight: i < steps.length-1 ? "1px solid var(--line)" : 0,
                 borderBottom: step===i ? "2px solid var(--accent-hi)" : "2px solid transparent",
                 marginBottom:-1
               }}>
            <div className="h-eyebrow" style={{marginBottom:4}}>Step 0{i+1}</div>
            <div style={{fontWeight:500, color: step===i ? "var(--text)" : "var(--text-mute)"}}>{s}</div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{
          marginBottom:20, padding:"12px 14px",
          border:"1px solid var(--bad)",
          borderRadius:4,
          background:"color-mix(in srgb, var(--bad) 10%, transparent)",
          fontSize:13, color:"var(--bad)"
        }}>{error}</div>
      )}

      {step === 0 && <AnalyzerUpload onNext={(id) => { setJobId(id); setStep(1); }} onError={setError}/>}
      {step === 1 && <AnalyzerExtract jobId={jobId} onNext={()=>setStep(2)} onError={setError}/>}
      {step === 2 && <AnalyzerReview onNext={()=>setStep(3)}/>}
      {step === 3 && <AnalyzerScore jobId={jobId}/>}
    </div>
  );
}

export function AnalyzerUpload({ onNext, onError }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  async function handleFile(file) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      onError && onError("Please upload a PDF file.");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      onError && onError("File exceeds 50 MB limit.");
      return;
    }
    onError && onError(null);
    setUploading(true);
    try {
      if (isDemoMode()) {
        setTimeout(() => { setUploading(false); onNext(null); }, 800);
        return;
      }
      const fd = new FormData();
      fd.append("deck", file);
      const resp = await apiPost("/analyzer/upload/", fd);
      setUploading(false);
      onNext(resp.job_id);
    } catch (err) {
      setUploading(false);
      onError && onError(err instanceof ApiError ? (err.body?.error || err.message) : (err.message || "Upload failed."));
    }
  }

  return (
    <div className="grid" style={{gridTemplateColumns:"1.5fr 1fr", gap:24}}>
      <label
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        onDragOver={(e) => e.preventDefault()}
        className="card"
        style={{
          padding:"60px 40px", textAlign:"center",
          border:"1px dashed var(--line-strong)",
          background:"var(--surface)",
          cursor: uploading ? "progress" : "pointer",
          opacity: uploading ? 0.6 : 1,
          display: "block",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={(e) => handleFile(e.target.files[0])}
          style={{display: "none"}}
        />
        <Ico n="upload" size={36}/>
        <h3 className="h-display" style={{fontSize:"var(--text-2xl)", marginTop:16}}>
          {uploading ? "Uploading…" : "Drop a PDF here, or click to browse."}
        </h3>
        <p className="dim" style={{fontSize:14, marginTop:10}}>Pitch deck · executive summary · teaser · up to 50MB</p>
        {!uploading && (
          <div className="row" style={{justifyContent:"center", marginTop:24, gap:12}}>
            <button type="button" className="btn btn--primary" onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}>Choose file</button>
            {isDemoMode() && (
              <button type="button" className="btn btn--ghost" onClick={(e) => { e.preventDefault(); onNext(null); }}>Simulate upload</button>
            )}
          </div>
        )}
      </label>
      <div className="stack">
        <div className="card">
          <div className="h-eyebrow">Before you upload</div>
          <ul style={{paddingLeft:16, marginTop:10, lineHeight:1.8, fontSize:13, color:"var(--text-mute)"}}>
            <li>Include the slide with your team</li>
            <li>Show your clinical/regulatory stage</li>
            <li>State the raise and use of funds</li>
            <li>Name comparables if you have them</li>
          </ul>
        </div>
        <div className="card">
          <div className="h-eyebrow">Privacy</div>
          <p style={{fontSize:13, color:"var(--text-mute)", marginTop:10, lineHeight:1.6}}>
            Your deck is encrypted at rest and only readable by the evaluators you share it with. It is never used to train third-party models.
          </p>
        </div>
      </div>
    </div>
  );
}

export function AnalyzerExtract({ jobId, onNext, onError }) {
  const [job, setJob] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!jobId) return; // demo mode / skipped
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const j = await apiGet(`/analyzer/jobs/${jobId}/`);
        if (cancelled) return;
        setJob(j);
        if (j.status === "complete" && j.analysis_id) {
          try { localStorage.setItem("vz-analysis-id", String(j.analysis_id)); } catch { /* ignore */ }
          // Fetch the completed analysis so we can hand it straight to the review step
          try {
            const analysis = await apiGet(`/analyses/${j.analysis_id}/`);
            if (!cancelled) setPreview(analysis);
          } catch { /* ignore */ }
          onNext && onNext();
          return;
        }
        if (j.status === "failed") {
          onError && onError(j.error_message || "Analysis failed.");
          return;
        }
        timer = setTimeout(poll, 3000);
      } catch (err) {
        if (cancelled) return;
        onError && onError(err instanceof ApiError ? (err.body?.error || err.message) : err.message);
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [jobId]);

  // Demo fallback: show the rich Helix mock so the design preview still
  // illustrates what this screen looks like when fully populated.
  const demoFields = [
    { k: "Company name",       v: "Helix Therapeutics",        c: .98 },
    { k: "Founding year",      v: "2022",                      c: .94 },
    { k: "Headquarters",       v: "Cambridge, MA",             c: .91 },
    { k: "Modality",           v: "mRNA lipid nanoparticle",   c: .88 },
    { k: "Indication",         v: "Solid tumors · HER2+",      c: .83 },
    { k: "Stage",              v: "Series A",                  c: .96 },
    { k: "Raise",              v: "$18M",                      c: .92 },
  ];

  const progressTotal = job?.progress_total || (jobId ? 0 : 28);
  const progressCurrent = job?.progress_current || 0;
  const progressPct = progressTotal > 0
    ? Math.min(100, Math.round((progressCurrent / progressTotal) * 100))
    : (jobId ? 8 : 82);

  const statusLabel = {
    pending: "Queued",
    running: "Running",
    complete: "Complete",
    failed: "Failed",
  }[job?.status] || (jobId ? "Starting…" : "—");

  // Real extracted fields become available as soon as the job completes.
  const realFields = preview?.analysis_fields || [];

  return (
    <div className="stack">
      <div className="card">
        <div className="row" style={{marginBottom:18}}>
          <div>
            <div className="h-eyebrow">
              {jobId
                ? (progressTotal ? `Extracting · ${progressCurrent} of ${progressTotal}` : "Extracting…")
                : "Extracting · 23 of 28 fields"}
            </div>
            <div style={{fontSize:"var(--text-lg)", marginTop:4}}>
              {jobId ? `Job #${jobId} · ${statusLabel}` : "helix-therapeutics-seriesA-v4.pdf · 28 slides · 4.2MB"}
            </div>
          </div>
          <div className="spacer"/>
          <div className="pill pill--accent"><i className="dot"/>Prompt set v2.41</div>
        </div>
        <div className="bar"><i style={{width:`${progressPct}%`}}/></div>
        {!jobId && (
          <div className="row" style={{justifyContent:"flex-end", marginTop:12}}>
            <button className="btn btn--ghost" onClick={() => onNext && onNext()}>Skip to review →</button>
          </div>
        )}
      </div>

      <div className="grid" style={{gridTemplateColumns:"1.4fr 1fr", gap:24, alignItems:"start"}}>
        <div className="card card--flush">
          <div className="card-header">
            <div>
              <div className="eyebrow">Extracted fields</div>
              <div className="title">{jobId ? (realFields.length ? "Parsed from this deck" : "Populates as the job completes…") : "First pass · confidence-ranked"}</div>
            </div>
          </div>
          {jobId && realFields.length === 0 ? (
            <div className="dim" style={{padding:"32px 20px", fontSize:13, textAlign:"center", fontStyle:"italic"}}>
              {job?.status === "complete"
                ? "No structured fields were extracted from this deck."
                : "Waiting for the first pass to complete…"}
            </div>
          ) : jobId ? (
            <table className="t">
              <tbody>
                {realFields.map(f => (
                  <tr key={f.label}>
                    <td style={{width:"38%", color:"var(--text-mute)"}}>{f.label}</td>
                    <td>{f.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="t">
              <tbody>
                {demoFields.map(f => (
                  <tr key={f.k}>
                    <td style={{width:"38%", color:"var(--text-mute)"}}>{f.k}</td>
                    <td>{f.v}</td>
                    <td style={{textAlign:"right", width:140}}>
                      <div style={{display:"inline-flex", alignItems:"center", gap:8}}>
                        <div style={{width:60, height:3, background:"var(--line)", borderRadius:2, overflow:"hidden"}}>
                          <div style={{width:`${f.c*100}%`, height:"100%", background: f.c < .6 ? "var(--warn)" : "var(--accent-hi)"}}/>
                        </div>
                        <span className="num dim" style={{fontSize:12}}>{f.c.toFixed(2)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="stack">
          <div className="card">
            <div className="h-eyebrow">Live status</div>
            <div className="mono" style={{fontSize:12, lineHeight:1.8, color:"var(--text-mute)", marginTop:10}}>
              {jobId ? (
                <>
                  <div>→ Job {jobId} · {statusLabel}</div>
                  {progressTotal > 0 && <div>→ {progressCurrent} of {progressTotal} steps complete</div>}
                  {job?.status === "complete" && <div>✓ Analysis ready</div>}
                </>
              ) : (
                <>
                  <div>✓ parsed 28 slides</div>
                  <div>✓ found team section (slide 18)</div>
                  <div>✓ detected IP claim (slide 22)</div>
                  <div>→ retrieving patent records…</div>
                  <div className="faint">↳ filtering relevance…</div>
                </>
              )}
            </div>
          </div>
          <button
            className="btn btn--primary"
            style={{justifyContent:"center"}}
            onClick={onNext}
            disabled={jobId && job?.status !== "complete"}
          >
            {jobId ? (job?.status === "complete" ? "Review extraction →" : "Waiting for job to finish…") : "Review extraction →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AnalyzerReview({ onNext }) {
  // Local resolution state — these decisions don't persist yet; they just
  // advance the UI and illustrate which uncertainties the analyst addressed.
  const [decisions, setDecisions] = useState({});
  const [missing, setMissing] = useState(null);

  useEffect(() => {
    let analysisId = null;
    try { analysisId = localStorage.getItem("vz-analysis-id"); } catch { /* ignore */ }
    if (!analysisId) return;
    let cancelled = false;
    apiGet(`/analyses/${analysisId}/`)
      .then(data => { if (!cancelled) setMissing(data.missing || []); })
      .catch(() => { if (!cancelled) setMissing([]); });
    return () => { cancelled = true; };
  }, []);

  // If we have a real analysis loaded, show its real missing fields. Otherwise
  // fall back to the MOCK list so the demo preview still illustrates the screen.
  const items = missing != null ? missing.slice(0, 8) : MOCK.missing.slice(0, 3);
  const headline = missing != null
    ? (items.length === 0 ? "No uncertainties flagged — ready to score." : `We flagged ${items.length} ${items.length === 1 ? "uncertainty" : "uncertainties"}. Resolve before scoring?`)
    : "We flagged 3 uncertainties. Resolve before scoring?";

  return (
    <div className="card">
      <div className="h-eyebrow">Review</div>
      <h3 className="h-display" style={{fontSize:"var(--text-2xl)", margin:"8px 0 16px"}}>{headline}</h3>
      {items.length === 0 ? (
        <p className="dim" style={{fontSize:14, marginBottom:24, fontStyle:"italic"}}>
          All essential fields were provided in the deck. You can proceed to the score.
        </p>
      ) : items.map((m, i) => {
        const d = decisions[i];
        return (
          <div key={i} style={{padding:"16px 0", borderBottom: i < items.length - 1 ? "1px solid var(--line)" : 0, display:"flex", gap:16, alignItems:"center", opacity: d ? 0.6 : 1}}>
            <div className="mono" style={{color:"var(--text-dim)", width:24}}>{String(i + 1).padStart(2, "0")}</div>
            <div style={{flex:1, fontSize:14}}>
              {m}
              {d && <span className="dim" style={{marginLeft:10, fontSize:12}}>· {d === "ask" ? "Will ask founder" : "Skipped"}</span>}
            </div>
            <button
              className={"btn " + (d === "ask" ? "btn--primary" : "btn--ghost")}
              onClick={() => setDecisions({...decisions, [i]: d === "ask" ? undefined : "ask"})}
            >Ask founder</button>
            <button
              className={"btn " + (d === "skip" ? "btn--primary" : "btn--quiet")}
              onClick={() => setDecisions({...decisions, [i]: d === "skip" ? undefined : "skip"})}
            >Skip</button>
          </div>
        );
      })}
      <div className="row" style={{marginTop:24}}>
        <div className="spacer"/>
        {items.length > 0 && <button className="btn btn--ghost" onClick={onNext} title="Generate the score even with unresolved gaps">Score with gaps</button>}
        <button className="btn btn--primary" onClick={onNext}>{items.length === 0 ? "Continue to score →" : "Generate score →"}</button>
      </div>
    </div>
  );
}

export function AnalyzerScore({ jobId }) {
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    let analysisId = null;
    try { analysisId = localStorage.getItem("vz-analysis-id"); } catch { /* ignore */ }
    if (!analysisId) return;
    apiGet(`/analyses/${analysisId}/`).then(setAnalysis).catch(() => {});
  }, [jobId]);

  const ias = analysis?.ias ?? 82;
  const name = analysis?.name || "Helix Therapeutics";
  const openFullAnalysis = () => navigate("results");

  return (
    <div className="card" style={{padding:"48px", textAlign:"center"}}>
      <IASMeter value={ias} size={260}/>
      <h3 className="h-display" style={{fontSize:"var(--text-3xl)", marginTop:16}}>{name} · IAS {Math.round(ias)}</h3>
      <p className="dim" style={{maxWidth:480, margin:"12px auto 24px", lineHeight:1.6}}>
        {analysis
          ? "Score ready. Open the full analysis to review the thesis, evidence, and peer comparison."
          : "Top-quartile across science and market. Clinical and IP pull the overall down — flagged as diligence priorities."}
      </p>
      <div className="row" style={{justifyContent:"center", gap:12}}>
        <button
          className="btn btn--ghost"
          onClick={() => {
            try {
              const aid = localStorage.getItem("vz-analysis-id");
              if (aid) location.href = `/dbview/output/${aid}/report/`;
            } catch { /* ignore */ }
          }}
          disabled={!analysis}
          title={analysis ? "Open the full Django memo view" : "Finish extraction first"}
        >Download memo</button>
        <button className="btn btn--primary" onClick={openFullAnalysis} disabled={!analysis}>Open full analysis →</button>
      </div>
    </div>
  );
}
